
import React, { useState, useEffect } from 'react';
import { VenueData } from '@/components/admin/types';
import { supabase } from '@/integrations/supabase/client';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from '@/hooks/use-toast';

export interface VenueAdminProps {
  conferenceId: string | undefined;
}

const VenueAdmin: React.FC<VenueAdminProps> = ({ conferenceId }) => {
  const [venueData, setVenueData] = useState<VenueData>({
    id: undefined,
    conference_id: conferenceId || null,
    venue_name: 'The Great Hall',
    address: 'Hornton St, Kensington',
    city: 'London',
    state: '',
    postal_code: 'W8 7NX',
    country: 'United Kingdom',
    capacity: '500',
    description: 'Located in the Kensington Conference and Events Centre',
    image_url: '',
    google_maps_url: 'https://goo.gl/maps/example',
    contact_email: 'info@aiconference.london',
    contact_phone: '+442037735519',
    hours: '9:00 AM - 5:00 PM',
    nearby_accommodations: '',
    transportation_info: '',
    floor_plan_url: '',
    virtual_tour_url: '',
    accessibility_info: '',
    parking_info: ''
  });
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const fetchVenueData = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('venue_details')
          .select('*')
          .eq('conference_id', conferenceId || '')
          .maybeSingle();

        if (error) {
          console.error("Error fetching venue data:", error);
          toast({
            title: "Error",
            description: `Failed to load venue data: ${error.message}`,
            variant: "destructive"
          });
        } else if (data) {
          setVenueData(data as unknown as VenueData);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchVenueData();
  }, [conferenceId, toast]);

  const updateVenueData = (newData: Partial<VenueData>) => {
    setVenueData(prev => ({
      ...prev,
      ...newData
    }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    updateVenueData({ [name]: value });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const venueDataToSubmit = {
        ...venueData,
        conference_id: conferenceId || null
      };

      // Use RLS override for admin operations
      let response;
      if (venueData.id) {
        response = await supabase
          .from('venue_details')
          .update(venueDataToSubmit)
          .eq('id', venueData.id);
      } else {
        response = await supabase
          .from('venue_details')
          .insert([venueDataToSubmit]);
      }

      if (response.error) {
        console.error("Error saving venue data:", response.error);
        toast({
          title: "Error",
          description: `Failed to save venue data: ${response.error.message}`,
          variant: "destructive"
        });
      } else {
        toast({
          title: "Success",
          description: 'Venue data saved successfully!'
        });
        if (!venueData.id && response.data && response.data.length > 0) {
          updateVenueData({ id: response.data[0].id });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-white">Venue Information</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          type="text"
          name="venue_name"
          placeholder="Venue Name"
          value={venueData.venue_name}
          onChange={handleInputChange}
        />
        <Input
          type="text"
          name="address"
          placeholder="Address"
          value={venueData.address}
          onChange={handleInputChange}
        />
        <Input
          type="text"
          name="city"
          placeholder="City"
          value={venueData.city}
          onChange={handleInputChange}
        />
        <Input
          type="text"
          name="state"
          placeholder="State"
          value={venueData.state}
          onChange={handleInputChange}
        />
        <Input
          type="text"
          name="postal_code"
          placeholder="Postal Code"
          value={venueData.postal_code}
          onChange={handleInputChange}
        />
        <Input
          type="text"
          name="country"
          placeholder="Country"
          value={venueData.country}
          onChange={handleInputChange}
        />
        <Input
          type="text"
          name="capacity"
          placeholder="Capacity"
          value={venueData.capacity}
          onChange={handleInputChange}
        />
        <Input
          type="text"
          name="image_url"
          placeholder="Image URL"
          value={venueData.image_url}
          onChange={handleInputChange}
        />
        <Input
          type="text"
          name="google_maps_url"
          placeholder="Google Maps URL"
          value={venueData.google_maps_url || ''}
          onChange={handleInputChange}
        />
        <Input
          type="text"
          name="contact_email"
          placeholder="Contact Email"
          value={venueData.contact_email || ''}
          onChange={handleInputChange}
        />
        <Input
          type="text"
          name="contact_phone"
          placeholder="Contact Phone"
          value={venueData.contact_phone || ''}
          onChange={handleInputChange}
        />
        <Input
          type="text"
          name="hours"
          placeholder="Hours"
          value={venueData.hours || ''}
          onChange={handleInputChange}
        />
        <Input
          type="text"
          name="nearby_accommodations"
          placeholder="Nearby Accommodations"
          value={venueData.nearby_accommodations || ''}
          onChange={handleInputChange}
        />
        <Input
          type="text"
          name="transportation_info"
          placeholder="Transportation Info"
          value={venueData.transportation_info || ''}
          onChange={handleInputChange}
        />
        <Input
          type="text"
          name="floor_plan_url"
          placeholder="Floor Plan URL"
          value={venueData.floor_plan_url || ''}
          onChange={handleInputChange}
        />
        <Input
          type="text"
          name="virtual_tour_url"
          placeholder="Virtual Tour URL"
          value={venueData.virtual_tour_url || ''}
          onChange={handleInputChange}
        />
        <Input
          type="text"
          name="accessibility_info"
          placeholder="Accessibility Info"
          value={venueData.accessibility_info || ''}
          onChange={handleInputChange}
        />
        <Input
          type="text"
          name="parking_info"
          placeholder="Parking Info"
          value={venueData.parking_info || ''}
          onChange={handleInputChange}
        />
      </div>

      <Textarea
        name="description"
        placeholder="Description"
        value={venueData.description || ''}
        onChange={handleInputChange}
        className="min-h-[100px]"
      />

      <Button 
        onClick={handleSubmit} 
        disabled={loading}
        className="bg-purple-600 hover:bg-purple-700"
      >
        {loading ? 'Saving...' : 'Save Venue Data'}
      </Button>
    </div>
  );
};

export default VenueAdmin;
